import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

export default function OrganizationOccupationalProfiles({ organizationId }) {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, [organizationId]);

  function fetchData() {
    setLoading(true);
    Promise.all([
      axios.post(
        `/organizations/${organizationId}/retrieve-occupational-profiles/`,
        { page: 0, page_size: 500 }
      ),
      axios.post(`/retrieve-occupational-profiles/`, {
        scope: "active",
        original: "true",
        page: 0,
        page_size: 500,
      }),
    ])
      .then(([assignedRes, catalogRes]) => {
        const joined = assignedRes.data.response[0]?.data || [];
        const catalog = catalogRes.data.response[0]?.data || [];
        const catalogById = Object.fromEntries(
          catalog.map((profile) => [String(profile.id), profile])
        );
        setAssigned(
          joined.map((row) => ({
            ...row,
            occupational_profile:
              row.occupational_profile ||
              catalogById[String(row.occupational_profile_id)] ||
              null,
          }))
        );
        const assignedProfileIds = new Set(
          joined.map((row) => String(row.occupational_profile_id))
        );
        setAvailable(
          catalog
            .filter((profile) => !assignedProfileIds.has(String(profile.id)))
            .map((profile) => ({
              value: String(profile.id),
              label: profile.original_name || profile.name || profile.id,
            }))
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function addProfile() {
    if (!selectedId) return;
    setBusyKey(`add-${selectedId}`);
    axios
      .post(`/organizations/${organizationId}/occupational-profiles/`, {
        occupational_profile_id: selectedId,
      })
      .then(() => {
        setSelectedId(null);
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function removeProfile(joinId) {
    setBusyKey(`remove-${joinId}`);
    axios
      .delete(
        `/organizations/${organizationId}/occupational-profiles/${joinId}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Title order={5}>{t("cs:profile_plural")}</Title>
      <Can permission={PERMISSIONS.ORG_OCCUPATIONAL_PROFILES_MANAGE}>
        <Card>
          <Flex gap="sm" align="flex-end">
            <Select
              style={{ flex: 1 }}
              searchable
              clearable
              placeholder={t("cs:profile_singular")}
              data={available}
              value={selectedId}
              onChange={setSelectedId}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              disabled={!selectedId}
              loading={busyKey === `add-${selectedId}`}
              onClick={addProfile}
            >
              {t("add")}
            </Button>
          </Flex>
        </Card>
      </Can>

      <Stack gap="xs">
        {!assigned.length && <EmptyMessage />}
        {assigned.map((row) => {
          const profile = row.occupational_profile;
          const label =
            profile?.original_name ||
            profile?.name ||
            row.occupational_profile_id;
          return (
            <Card key={row.id}>
              <Flex gap="sm" align="center">
                <Text
                  fw={700}
                  style={{ flexGrow: 1 }}
                  component={profile?.id ? Link : "span"}
                  to={profile?.id ? `/profiles/${profile.id}` : undefined}
                  c="dark"
                >
                  {label}
                </Text>
                <Can permission={PERMISSIONS.ORG_OCCUPATIONAL_PROFILES_MANAGE}>
                  <ActionIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="red"
                    title={t("remove")}
                    loading={busyKey === `remove-${row.id}`}
                    onClick={() => removeProfile(row.id)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Can>
              </Flex>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
}
